import { Injectable, OnDestroy, Inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MAP_CLIENT_SCRIPTS } from 'src/app/maps/map-client-scripts.constants';
import { LOCALE_MAP_CLIENT_MAP, LocaleMapId } from 'src/app/maps/maps.models';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

/** Security: allowlist of trusted hostnames for external map scripts (XSSi prevention). */
const ALLOWED_SCRIPT_HOSTS: ReadonlyArray<string> = ['maps.googleapis.com', 'webapi.amap.com'];

@Injectable({
  providedIn: 'root',
})
export class MapsScriptsService implements OnDestroy {
  private scripts: any = {};
  private _scriptLoaded = new BehaviorSubject(false);

  public scriptLoaded = this._scriptLoaded.asObservable();

  constructor(@Inject(CbreLoggerToken) private logger: CbreLogger) {
    MAP_CLIENT_SCRIPTS.forEach(script => {
      this.scripts[script.name] = {
        loaded: false,
        src: script.src,
      };
    });
  }

  ngOnDestroy(): void {
    this._scriptLoaded.complete();
  }

  public initMapScripts(userLocaleMapId: LocaleMapId) {
    const mapClientKey = LOCALE_MAP_CLIENT_MAP[userLocaleMapId];
    if (mapClientKey != null) {
      this.load(mapClientKey)
        .then(() => {
          this.logger.debug(`Map ${mapClientKey} client scripts loaded successfully`);
          this._scriptLoaded.next(true);
        })
        .catch(error => this.logger.warn(`Map ${mapClientKey} scripts failed loading: ${error.error}`));
    } else {
      this._scriptLoaded.next(true);
    }
  }

  // load a single or multiple scripts
  private load(...scriptsToLoad: string[]) {
    const promises: any[] = [];
    scriptsToLoad.forEach(script => promises.push(this.loadScript(script)));
    return Promise.all(promises);
  }

  private loadScript(name: string) {
    return new Promise((resolve, reject) => {
      // TODO: research and implement the usage of "import" instead of modifying the document
      if (!this.scripts[name].loaded) {
        const src: string = this.scripts[name].src;

        // Security: validate script src against trusted host allowlist to
        // prevent Cross-Site Script Inclusion (XSSi) via unvalidated URLs.
        let validatedUrl: URL;
        try {
          validatedUrl = new URL(src);
          if (!ALLOWED_SCRIPT_HOSTS.includes(validatedUrl.hostname)) {
            return reject({ script: name, loaded: false, status: 'Blocked', error: 'Untrusted script host' });
          }
        } catch {
          return reject({ script: name, loaded: false, status: 'Blocked', error: 'Invalid script URL' });
        }

        let script = document.createElement('script');
        script.type = 'text/javascript';
        // Security: assign the serialized form of the already-validated URL
        // object (not the raw string) to make the sanitization boundary explicit
        // and prevent XSS via unsanitized dynamic HTML insertion.
        script.setAttribute('src', validatedUrl.href);
        script.async = true;
        script.onload = () => {
          this.scripts[name].loaded = true;
          resolve({ script: name, loaded: true, status: 'Loaded' });
        };
        script.onerror = (error: any) => reject({ script: name, loaded: false, status: 'Failed', error });
        document.getElementsByTagName('head')[0].appendChild(script);
      } else {
        // resolve if already loaded
        resolve({ script: name, loaded: true, status: 'Already Loaded' });
      }
    });
  }
}
